import {FormField} from '../../../types/form-builder.types';
import {cn, useLanguage} from '@shondalai/ui-commons';
import {File, Upload, X} from 'lucide-react';
import {useState} from 'react';

interface FileUploadFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

export function FileUploadField({ field, value, onChange }: FileUploadFieldProps) {
  const config = field.config;
  const { t } = useLanguage();
  const [uploading, setUploading] = useState(false);

  // Handle both File objects and uploaded file metadata
  const files = Array.isArray(value) ? value : value ? [value] : [];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    try {
      // Store actual File objects for backend upload, but also create preview data
      const fileData = selectedFiles.map(file => {
        // Store the actual File object and metadata
        return {
          file: file, // Actual File object for upload
          name: file.name,
          size: file.size,
          type: file.type,
          preview: URL.createObjectURL(file)
        };
      });

      if (config.multiple) {
        onChange([...files, ...fileData]);
      } else {
        onChange(fileData[0]);
      }
    } catch (err) {
      console.error('File selection failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index: number) => {
    if (config.multiple) {
      onChange(files.filter((_: any, i: number) => i !== index));
    } else {
      onChange(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' ' + t('COM_EASYFORMS_BYTES_SHORT', 'B');
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' ' + t('COM_EASYFORMS_KB_SHORT', 'KB');
    return (bytes / (1024 * 1024)).toFixed(1) + ' ' + t('COM_EASYFORMS_MB_SHORT', 'MB');
  };

  return (
    <div className="space-y-2.5">
      {/* Upload Button */}
      <label
        htmlFor={field.id}
        className={cn(
          "easyforms-file-input",
          uploading && "opacity-50 cursor-not-allowed"
        )}
      >
        <Upload className="w-4 h-4" strokeWidth={1.5} />
        <span className="text-sm font-light">
          {uploading
            ? t('COM_EASYFORMS_UPLOADING', 'Uploading...')
            : config.multiple
              ? t('COM_EASYFORMS_CHOOSE_FILES_OR_DRAG', 'Choose files or drag here')
              : t('COM_EASYFORMS_CHOOSE_FILE_OR_DRAG', 'Choose file or drag here')}
        </span>
        <input
          id={field.id}
          type="file"
          onChange={handleFileChange}
          accept={config.accept}
          multiple={config.multiple}
          className="hidden"
          disabled={uploading}
        />
      </label>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file: any, index: number) => (
            <div
              key={index}
              className={cn(
                "flex items-center justify-between gap-3 p-2.5 rounded-md",
                "bg-muted/30 border border-border/30",
                "transition-colors duration-200"
              )}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <File className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate font-medium">{file.name}</p>
                  {file.size && (
                    <p className="text-xs text-muted-foreground/70 font-light">
                      {formatFileSize(file.size)}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                className={cn(
                  "flex-shrink-0 p-1 rounded",
                  "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                  "transition-colors duration-200"
                )}
                aria-label={t('COM_EASYFORMS_REMOVE_FILE', 'Remove file')}
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
